import Api from '../api';
import {
  ADMIN_CREATE_TENANT, ADMIN_CREATE_TENANT_URL,
  ADMIN_SETUP_TENANT_BILLING, ADMIN_SETUP_TENANT_BILLING_URL,
  RESUME_ONBOARDING, RESUME_ONBOARDING_URL,
  GET_ONBOARDING_STATUS, GET_ONBOARDING_STATUS_URL,
  LIST_ONBOARDINGS, LIST_ONBOARDINGS_URL,
  GET_ONBOARDING_CONFIG, GET_ONBOARDING_CONFIG_URL,
  UPDATE_ONBOARDING_CONFIG, UPDATE_ONBOARDING_CONFIG_URL
} from '../lib/constant';
import {handleAPIDispatch} from '../lib/utils';

// Create a new tenant via admin onboarding (SMS workspace).
export function adminCreateTenant(params) {
  const body = {
    customer_name: params.customerName,
    company_name: params.companyName,
    email: params.email,
    mobile: params.mobile,
    workspace_type: 'sms',
    // Org address/city ride in the onboarding call itself (stored in the
    // tenant config JSON server-side); only send when filled — the
    // transcoder is fine with omitted optionals.
    ...(params.city ? {city: params.city} : {}),
    ...(params.address ? {address: params.address} : {}),
    // Complete-info onboarding: tenant_type=active makes the backend skip
    // the pre_approved trial/demo seed (demo sender, demo template, demo
    // balance) and instead create the first sender_id + a default message
    // type from these params. The remaining senders/types are applied by
    // the UI's post-setup phase.
    ...(params.tenantType ? {tenant_type: params.tenantType} : {}),
    // seed_profile decouples config provisioning from lifecycle:
    // "explicit" = provision from these params, never the demo/trial seed.
    ...(params.seedProfile ? {seed_profile: params.seedProfile} : {}),
    ...(params.senderIds?.length ? {sender_ids: params.senderIds} : {}),
    ...(params.dltPrincipalEntityId ? {dlt_principal_entity_id: params.dltPrincipalEntityId} : {}),
    // Billing: plan + optional enterprise rate override + one wallet per
    // selected SMS type, each with its own initial credits and rate. The
    // backend's step_billing creates these instead of the single default
    // trial wallet when sms_billing is non-empty.
    ...(params.planCode ? {plan_code: params.planCode} : {}),
    ...(params.customSmsRate ? {custom_sms_rate: params.customSmsRate} : {}),
    ...(params.smsBilling?.length ? {sms_billing: params.smsBilling} : {}),
    // Explicit opt-out: step_billing is marked "skipped"; billing is set up
    // later via adminSetupTenantBilling.
    ...(params.skipBilling ? {skip_billing: true} : {}),
    // Welcome mail off unless a caller explicitly asks for it. The proto
    // default is false so the field must be sent to have any effect, and this
    // body is a whitelist — a caller passing suppressWelcomeEmail without this
    // line is silently ignored, and the mail goes out. Irreversible, to a real
    // inbox, so the failure has to be "no mail" and not "mail".
    ...(params.suppressWelcomeEmail === false ? {} : {suppress_welcome_email: true})
  };
  const request = Api.post(ADMIN_CREATE_TENANT_URL, body);
  return handleAPIDispatch(request, ADMIN_CREATE_TENANT);
}

// Standalone billing setup / reconfiguration for an existing tenant.
// Idempotent: ensures org + customer + subscription (plan_code, optional
// custom_sms_rate) and creates any missing per-type wallets from smsBilling
// ([{sms_type, initial_credits, rate}]). Existing wallets are never topped up.
export function adminSetupTenantBilling(tenantId, params) {
  const body = {
    ...(params.planCode ? {plan_code: params.planCode} : {}),
    ...(params.customSmsRate ? {custom_sms_rate: params.customSmsRate} : {}),
    ...(params.smsBilling?.length ? {sms_billing: params.smsBilling} : {})
  };
  const url = ADMIN_SETUP_TENANT_BILLING_URL.replace('{tenantId}', tenantId);
  const request = Api.post(url, body);
  return handleAPIDispatch(request, ADMIN_SETUP_TENANT_BILLING);
}

export function resumeOnboarding(onboardingId) {
  const url = RESUME_ONBOARDING_URL.replace('{onboardingId}', onboardingId);
  const request = Api.post(url, {});
  return handleAPIDispatch(request, RESUME_ONBOARDING);
}

export function getOnboardingStatus(onboardingId) {
  const url = GET_ONBOARDING_STATUS_URL.replace('{onboardingId}', onboardingId);
  const request = Api.get(url);
  return handleAPIDispatch(request, GET_ONBOARDING_STATUS);
}

export function listOnboardings(limit = 50, offset = 0) {
  const url = LIST_ONBOARDINGS_URL + `?limit=${limit}&offset=${offset}`;
  const request = Api.get(url);
  return handleAPIDispatch(request, LIST_ONBOARDINGS);
}

export function getOnboardingConfig(workspaceType) {
  const url = GET_ONBOARDING_CONFIG_URL.replace('{workspaceType}', workspaceType);
  const request = Api.get(url);
  return handleAPIDispatch(request, GET_ONBOARDING_CONFIG);
}

export function updateOnboardingConfig(workspaceType, body) {
  const url = UPDATE_ONBOARDING_CONFIG_URL.replace('{workspaceType}', workspaceType);
  const request = Api.put(url, {workspace_type: workspaceType, ...body});
  return handleAPIDispatch(request, UPDATE_ONBOARDING_CONFIG);
}
